import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDate, formatIDR } from "./format";

const NAVY = [15, 23, 42];
const BLUE = [30, 64, 175];
const SLATE = [100, 116, 139];
const BORDER = [148, 163, 184];

export function generateQuotationPDF({ quote, company, signatory, preview }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const PW = 210;
  const M = 14;

  // ---- Header ----
  let hx = M;
  if (company?.logo_base64) {
    try { doc.addImage(company.logo_base64, "PNG", M, 12, 22, 22); hx = M + 26; } catch (e) {}
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...BLUE);
  doc.text((company?.company_name || "PT. FUJI INDO SURYA").toUpperCase(), hx, 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  const addr = doc.splitTextToSize(company?.address || "", 110);
  let ay = 23;
  addr.slice(0, 3).forEach((ln) => { doc.text(ln, hx, ay); ay += 4; });
  if (company?.phone) doc.text(`Tel: ${company.phone}${company?.fax ? "  Fax: " + company.fax : ""}`, hx, ay);
  if (company?.email) doc.text(`E-mail: ${company.email}`, hx, ay + 4);

  // ---- QUOTATION title ----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.setTextColor(...BLUE);
  doc.text("QUOTATION", PW - M, 22, { align: "right" });

  // ---- Meta ----
  const metaTop = 32;
  const metaX = PW - M - 78;
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  const metaRows = [
    ["Date of Quote", formatDate(quote.quote_date)],
    ["Quote No.", quote.quote_number],
    ["Valid Until", quote.valid_until ? formatDate(quote.valid_until) : "-"],
  ];
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  let my = metaTop;
  metaRows.forEach(([k, v]) => {
    doc.rect(metaX, my, 78, 6);
    doc.setFont("helvetica", "bold");
    doc.text(k, metaX + 2, my + 4);
    doc.setFont("helvetica", "normal");
    doc.text(":", metaX + 32, my + 4);
    doc.text(String(v), metaX + 34, my + 4);
    my += 6;
  });

  // ---- Divider ----
  const dvY = Math.max(my, 55) + 4;
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.5);
  doc.line(M, dvY, PW - M, dvY);

  // ---- To / Attn ----
  let ry = dvY + 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text("To     :", M, ry);
  doc.setFontSize(11);
  doc.text((quote.customer_name || "-").toUpperCase(), M + 15, ry);
  ry += 5;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  if (quote.customer_address) {
    const cl = doc.splitTextToSize(quote.customer_address, 130);
    cl.slice(0, 3).forEach((ln) => { doc.text(ln, M + 15, ry); ry += 3.5; });
  }
  if (quote.customer_pic) {
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...NAVY);
    doc.text("Attn  :", M, ry + 1);
    doc.setFont("helvetica", "normal");
    doc.text(`${quote.customer_pic}${quote.customer_phone ? "  (" + quote.customer_phone + ")" : ""}`, M + 15, ry + 1);
    ry += 5;
  }

  // ---- Items table ----
  autoTable(doc, {
    startY: ry + 4,
    head: [["No.", "Description", "Unit", "Qty", "Price", "Total"]],
    body: quote.items.map((it, i) => [
      String(i + 1),
      it.description ? `${it.name}\n${it.description}` : it.name,
      it.unit || "PCS",
      String(it.quantity),
      formatIDR(it.price),
      formatIDR(Number(it.quantity) * Number(it.price)),
    ]),
    headStyles: { fillColor: [226, 232, 240], textColor: NAVY, fontSize: 9, halign: "center", fontStyle: "bold", lineColor: [148, 163, 184], lineWidth: 0.25 },
    bodyStyles: { fontSize: 9, textColor: NAVY, lineColor: [203, 213, 225], lineWidth: 0.2 },
    alternateRowStyles: { fillColor: [241, 245, 249] },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: "auto" },
      2: { cellWidth: 16, halign: "center" },
      3: { cellWidth: 15, halign: "right" },
      4: { cellWidth: 30, halign: "right" },
      5: { cellWidth: 32, halign: "right", fontStyle: "bold", textColor: NAVY },
    },
    margin: { left: M, right: M },
    theme: "grid",
  });

  // ---- Summary ----
  let fy = doc.lastAutoTable.finalY;
  const sumX = PW - M - 78;
  const showDisc = (quote.discount || 0) > 0;
  const rows = [
    ["Total Amount", formatIDR(quote.subtotal)],
    ...(showDisc ? [[
      quote.discount_type === "percent" ? `Discount (${quote.discount_value}%)` : "Discount",
      `- ${formatIDR(quote.discount)}`,
    ]] : []),
    ...(quote.include_ppn ? [[`VAT ${quote.ppn_percent}%`, formatIDR(quote.ppn)]] : []),
  ];
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  rows.forEach(([k, v]) => {
    doc.rect(sumX, fy, 78, 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...NAVY);
    doc.text(k, sumX + 2, fy + 3.5);
    doc.text(v, sumX + 76, fy + 3.5, { align: "right" });
    fy += 5;
  });
  doc.setFillColor(...BLUE);
  doc.rect(sumX, fy, 78, 8, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Grand Total", sumX + 2, fy + 5.5);
  doc.text(formatIDR(quote.total), sumX + 76, fy + 5.5, { align: "right" });
  fy += 8;

  // ---- Terms & Conditions (left) ----
  const startTC = doc.lastAutoTable.finalY + 6;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text("Terms & Condition", M, startTC);
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.4);
  doc.line(M, startTC + 1, M + 34, startTC + 1);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  const tc = quote.terms_conditions || (
    "1. Price include PPN 12%.\n" +
    "2. Payment Term: TT 30 Days after invoice received.\n" +
    "3. Validity Term: 30 Days from quotation date.\n" +
    "4. Delivery: 1-2 weeks after PO received."
  );
  const tcLines = doc.splitTextToSize(tc, 110);
  let tcY = startTC + 5;
  tcLines.forEach((ln) => { doc.text(ln, M, tcY); tcY += 4; });

  // ---- Auto-generated note (right, DI BAWAH Grand Total agar tidak menabrak) ----
  const noteX = PW - M - 80;
  const noteY = fy + 6; // fy sudah di ujung bawah blok Grand Total
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.3);
  doc.roundedRect(noteX, noteY, 80, 20, 2, 2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...NAVY);
  doc.text("CATATAN:", noteX + 3, noteY + 5);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(...SLATE);
  const autoNote = "Dokumen ini dibuat otomatis oleh sistem dan sah tanpa tanda tangan basah.";
  const noteLines = doc.splitTextToSize(autoNote, 74);
  let nY = noteY + 10;
  noteLines.forEach((ln) => { doc.text(ln, noteX + 3, nY); nY += 4; });

  // ---- Footer ----
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text(`Diterbitkan otomatis oleh ${company?.company_name || "FUJI INDO SURYA ERP SYSTEM"}.`, M, 290);

  const filename = `${quote.quote_number.replaceAll("/", "-")}.pdf`;
  if (preview) return { url: doc.output("bloburl"), filename };
  doc.save(filename);
}
